import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { formatCurrency, formatDate } from "@/lib/format";
import { Receipt } from "lucide-react";
import type { Invoice } from "@db/schema";

const columns: ColumnDef<Invoice>[] = [
  {
    key: "reference",
    header: "Invoice",
    cell: (row) => <span className="font-medium">{row.reference}</span>,
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => row.status.replace(/_/g, " "),
  },
  {
    key: "period",
    header: "Revenue Period",
    cell: (row) =>
      `${formatDate(row.revenuePeriodStart)} - ${formatDate(row.revenuePeriodEnd)}`,
  },
  {
    key: "total",
    header: "Total",
    cell: (row) => formatCurrency(row.totalAmount, row.currency),
  },
];

export default function ClientInvoices() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<Invoice[]>({
    queryKey: ["/api/portal/invoices"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filtered = data?.filter((invoice) => {
    if (!search) return true;
    return invoice.reference.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        description="See approved billing documents shared with your account."
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search invoices..."
        emptyIcon={Receipt}
        emptyTitle="No invoices"
        emptyDescription="Client-safe invoices will appear here."
      />
    </div>
  );
}
